<!-- Fixed Header -->
<nav
  class="fixed top-0 left-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"
>
  <div class="max-w-2xl mx-auto px-2 sm:px-0">
    <div class="flex items-center justify-between h-14">
      <div class="flex items-center min-w-0">
        <button (click)="goBack()" class="back-btn" title="Back to students">
          <lucide-icon
            [img]="icons.ArrowLeft"
            class="w-3.5 h-3.5"
          ></lucide-icon>
          <span class="hidden sm:inline">Students</span>
        </button>
        @if (submission$ | async; as sub) {
          <span class="ml-3 text-sm font-semibold text-gray-900 truncate">
            {{ sub.studentName }}
          </span>
        }
      </div>
      @if (submission$ | async; as sub) {
        @if (sub.gradePercentage !== null) {
          <div class="flex items-center gap-2 shrink-0 ml-4">
            <span class="text-xs text-gray-500 hidden sm:inline">
              {{ sub.gradePercentage | number: '1.0-0' }}%
            </span>
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
              [ngClass]="getGradeBadgeClasses(sub.gradePercentage)"
            >
              {{ getLetterGrade(sub.gradePercentage) }}
            </div>
          </div>
        }
      }
    </div>
  </div>
</nav>

<!-- Content area -->
<div class="pt-14">
  <div class="max-w-2xl mx-auto px-2 sm:px-0 py-6">
    <!-- Loading -->
    @if (loading$ | async) {
      <div class="flex items-center justify-center py-16 text-gray-500">
        <span class="text-sm">Loading submission...</span>
      </div>
    }

    <!-- Error -->
    @if (error$ | async; as err) {
      <div
        class="rounded-2xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm"
      >
        {{ err }}
      </div>
    }

    <!-- Submission content -->
    @if (submission$ | async; as submission) {
      <div class="space-y-4">
      <!-- Submission Metadata Card -->
      <div
        class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
      >
        <div class="grid grid-cols-2 sm:grid-cols-4">
          <!-- Started -->
          <div class="px-4 py-3">
            <div class="flex items-center gap-1.5 mb-1">
              <lucide-icon
                [img]="icons.Calendar"
                class="w-3.5 h-3.5 text-gray-400"
              ></lucide-icon>
              <span class="text-[0.65rem] font-semibold text-gray-400 uppercase tracking-wide">
                Started
              </span>
            </div>
            <div class="text-sm font-medium text-gray-900">
              {{ submission.startedAt | date: 'MMM d, y' }}
            </div>
            <div class="text-xs text-gray-500">
              {{ submission.startedAt | date: 'h:mm a' }}
            </div>
          </div>

          <!-- Submitted -->
          <div class="px-4 py-3">
            <div class="flex items-center gap-1.5 mb-1">
              <lucide-icon
                [img]="icons.Clock"
                class="w-3.5 h-3.5 text-gray-400"
              ></lucide-icon>
              <span class="text-[0.65rem] font-semibold text-gray-400 uppercase tracking-wide">
                Submitted
              </span>
            </div>
            @if (submission.submittedAt) {
              <div class="text-sm font-medium text-gray-900">
                {{ submission.submittedAt | date: 'MMM d, y' }}
              </div>
              <div class="text-xs text-gray-500">
                {{ submission.submittedAt | date: 'h:mm a' }}
              </div>
            } @else {
              <div class="text-sm font-medium text-gray-400">—</div>
            }
          </div>

          <!-- Duration -->
          <div class="px-4 py-3">
            <div class="flex items-center gap-1.5 mb-1">
              <lucide-icon
                [img]="icons.Timer"
                class="w-3.5 h-3.5 text-gray-400"
              ></lucide-icon>
              <span class="text-[0.65rem] font-semibold text-gray-400 uppercase tracking-wide">
                Duration
              </span>
            </div>
            <div class="text-sm font-medium text-gray-900">
              {{ getDuration(submission) }}
            </div>
          </div>

          <!-- Questions Answered -->
          <div class="px-4 py-3">
            <div class="flex items-center gap-1.5 mb-1">
              <lucide-icon
                [img]="icons.Hash"
                class="w-3.5 h-3.5 text-gray-400"
              ></lucide-icon>
              <span class="text-[0.65rem] font-semibold text-gray-400 uppercase tracking-wide">
                Answered
              </span>
            </div>
            <div class="text-sm font-medium text-gray-900">
              {{ submission.answeredCount }}/{{ submission.questionsCount }}
            </div>
            <div class="text-xs text-gray-500">questions</div>
          </div>
        </div>
      </div>

      <!-- Overall Feedback Card -->
      @if (submission.overallFeedback) {
        <div
          class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
        >
          <div class="px-4 py-3 bg-gray-50/50 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <lucide-icon
                [img]="icons.MessageSquare"
                class="w-4 h-4 text-violet-500"
              ></lucide-icon>
              <span class="font-semibold text-gray-800 text-sm">
                Overall Feedback
              </span>
            </div>
          </div>
          <div class="px-4 py-3">
            <p
              class="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap"
            >
              {{ submission.overallFeedback }}
            </p>
          </div>
        </div>
      }

      <!-- Strengths Card -->
      @if (submission.strengths.length) {
        <div
          class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
        >
          <div class="px-4 py-3 bg-green-50/50 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <lucide-icon
                [img]="icons.Lightbulb"
                class="w-4 h-4 text-green-500"
              ></lucide-icon>
              <span class="font-semibold text-gray-800 text-sm">
                Strengths
              </span>
              <span
                class="text-xs text-gray-500 bg-green-100 px-2 py-0.5 rounded-full"
              >
                {{ submission.strengths.length }}
              </span>
            </div>
          </div>
          <div class="px-4 py-3">
            <ul class="space-y-2">
              @for (strength of submission.strengths; track strength) {
                <li class="flex items-start gap-2 text-sm text-gray-700">
                  <lucide-icon
                    [img]="icons.CheckCircle2"
                    class="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5"
                  ></lucide-icon>
                  <span>{{ strength }}</span>
                </li>
              }
            </ul>
          </div>
        </div>
      }

      <!-- Areas for Improvement Card -->
      @if (submission.areasForImprovement.length) {
        <div
          class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
        >
          <div class="px-4 py-3 bg-amber-50/50 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <lucide-icon
                [img]="icons.Target"
                class="w-4 h-4 text-amber-500"
              ></lucide-icon>
              <span class="font-semibold text-gray-800 text-sm">
                Areas for Improvement
              </span>
              <span
                class="text-xs text-gray-500 bg-amber-100 px-2 py-0.5 rounded-full"
              >
                {{ submission.areasForImprovement.length }}
              </span>
            </div>
          </div>
          <div class="px-4 py-3">
            <ul class="space-y-2">
              @for (area of submission.areasForImprovement; track area) {
                <li class="flex items-start gap-2 text-sm text-gray-700">
                  <lucide-icon
                    [img]="icons.AlertTriangle"
                    class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"
                  ></lucide-icon>
                  <span>{{ area }}</span>
                </li>
              }
            </ul>
          </div>
        </div>
      }

      <!-- Questions Breakdown Card -->
      @if (submission.questions.length) {
        <div
          class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
        >
          <div class="px-4 py-3 bg-gray-50/50 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <lucide-icon
                [img]="icons.FileText"
                class="w-4 h-4 text-violet-500"
              ></lucide-icon>
              <span class="font-semibold text-gray-800 text-sm">
                Questions Breakdown
              </span>
              <span
                class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full"
              >
                {{ submission.questions.length }} questions
              </span>
            </div>
          </div>
          <div class="divide-y divide-gray-100">
            @for (
              question of getSortedQuestions(submission);
              track question.id;
              let idx = $index
            ) {
              @let answer = getAnswerForQuestion(submission, question.id);
              @let correct = isQuestionCorrect(answer);
              @let expanded = isQuestionExpanded(question.id);

              <div>
                <!-- Question Header (Accordion Toggle) -->
                <button
                  type="button"
                  (click)="toggleQuestion(question.id)"
                  class="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors cursor-pointer text-left"
                >
                  <!-- Correct/Incorrect icon -->
                  @if (answer?.pointsAwarded !== null) {
                    @if (correct) {
                      <lucide-icon
                        [img]="icons.CheckCircle"
                        class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                      ></lucide-icon>
                    } @else {
                      <lucide-icon
                        [img]="icons.XCircle"
                        class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                      ></lucide-icon>
                    }
                  } @else {
                    <span
                      class="w-5 h-5 flex items-center justify-center text-xs font-bold text-gray-400 flex-shrink-0 mt-0.5"
                    >
                      {{ idx + 1 }}
                    </span>
                  }

                  <span
                    class="flex-1 text-sm text-gray-700 font-medium line-clamp-2"
                  >
                    <span class="text-gray-500">{{ idx + 1 }}.</span>
                    {{ question.questionText }}
                  </span>

                  <div class="flex items-center gap-2 flex-shrink-0">
                    @if (answer?.pointsAwarded !== null) {
                      <span
                        class="text-sm font-semibold"
                        [ngClass]="
                          getScoreColorClass(
                            answer!.maxPoints > 0
                              ? (answer!.pointsAwarded! / answer!.maxPoints) *
                                  100
                              : 0
                          )
                        "
                      >
                        {{ answer?.pointsAwarded }}/{{ answer?.maxPoints }}
                      </span>
                    }
                    <lucide-icon
                      [img]="icons.ChevronDown"
                      class="w-4 h-4 text-gray-400 transition-transform duration-200"
                      [class.rotate-180]="expanded"
                    ></lucide-icon>
                  </div>
                </button>

                <!-- Question Details (Expanded Content) -->
                @if (expanded) {
                  <div class="px-4 pb-4 bg-gray-50/30 space-y-3">
                    <!-- Student's answer -->
                    <div class="answer-block">
                      <div class="answer-block-label">STUDENT'S ANSWER</div>
                      <p
                        class="text-sm"
                        [ngClass]="{
                          'text-green-700': correct,
                          'text-red-600':
                            !correct && answer?.pointsAwarded !== null,
                          'text-gray-700': answer?.pointsAwarded === null,
                        }"
                      >
                        {{ getStudentAnswerText(question, answer) }}
                      </p>
                    </div>

                    <!-- Correct answer (for MC if wrong) -->
                    @if (
                      question.type === 'multiple_choice' &&
                      !correct &&
                      answer?.pointsAwarded !== null
                    ) {
                      <div class="answer-block answer-block-correct">
                        <div class="answer-block-label text-green-600">
                          CORRECT ANSWER
                        </div>
                        <p class="text-sm text-green-700">
                          {{ getCorrectAnswerText(question) }}
                        </p>
                      </div>
                    }

                    <!-- AI Feedback -->
                    @if (answer?.feedback) {
                      <div
                        class="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap"
                      >
                        {{ answer?.feedback }}
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>
      } @else {
        <div
          class="bg-white border border-gray-200 rounded-2xl shadow-sm px-4 py-8 text-center"
        >
          <p class="text-gray-500 text-sm">
            No questions in this submission.
          </p>
        </div>
      }
    </div>
  }
  </div>
</div>
